import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import QuillTableBetter from "quill-table-better";
import {
    capturePasteData,
    isImagePasteCapture,
    normalizePastedImageFile,
    processCapturedPaste,
    stopPasteEvent,
} from "./quillEvents";
import { resolveEditorImageSrc } from "./resolveEditorImageSrc";
import { setQuillBusy } from "./setQuillBusy";
import {
    ensureSoftBreakRegistered,
    softBreakClipboardMatcher,
    softBreakKeyboardBinding,
} from "./softBreak";
import { QuillImageUploadFunction, QuillSelectKeyHandler } from "../types";

/** Quill default uploader only allows png/jpeg and inserts data: URLs — we replace that. */
const QUILL_UPLOADER_MIMETYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
    "image/gif",
    "image/bmp",
    "image/svg+xml",
];

const WEBP_QUALITY_DEF = 0.8;
const WEBP_MAXWIDTH_DEF = 1200;
const WEBP_MAXHEIGHT_DEF = 1200;
const TABLE_BETTER_REGISTERED_KEY = "__quill_table_better_registered__";
const SAFE_GET_COMPUTED_STYLE_PATCHED_KEY = "__safe_get_computed_style_patched__";
const TABLE_BETTER_DELETE_GUARD_PATCHED_KEY = "__table_better_delete_guard_patched__";
const TABLE_BETTER_CREATE_DEFAULTS_PATCHED_KEY = "__table_better_create_defaults_patched__";

/** Default formats allowed while a table cell is focused (quill-table-better WHITE_LIST). */
const TABLE_BETTER_TOOLBAR_WHITE_LIST = [
    "bold",
    "italic",
    "underline",
    "strike",
    "size",
    "color",
    "background",
    "font",
    "list",
    "header",
    "align",
    "link",
    "image",
] as const;

/** Custom toolbar actions that must stay clickable inside a single table cell. */
const TABLE_BETTER_TOOLBAR_CUSTOM_SINGLE = [
    "insertTemplateKey",
    "pasteFromClipboard",
] as const;

const TABLE_BETTER_ICON =
    '<svg viewbox="0 0 18 18">' +
    '<rect class="ql-stroke" height="12" width="12" x="3" y="3"></rect>' +
    '<line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line>' +
    '<line class="ql-stroke" x1="9" x2="9" y1="3" y2="15"></line>' +
    "</svg>";

const applyTableBetterIcon = () => {
    try {
        const icons = Quill.import("ui/icons") as Record<string, string>;
        icons["table-better"] = TABLE_BETTER_ICON;
    } catch {
        // ignore — Quill may not be ready yet
    }
};

const replaceTableBetterToolbarIcon = (editor: Quill) => {
    const toolbar = editor.getModule("toolbar") as { container?: HTMLElement } | null;
    const button = toolbar?.container?.querySelector("button.ql-table-better") as HTMLButtonElement | null;
    if (!button) return;

    const existingSelect = button.querySelector(".ql-table-select-container");
    const oldSvg = button.querySelector(":scope > svg") as SVGElement | null;
    const alreadyReplaced =
        !!oldSvg &&
        oldSvg.querySelectorAll(".ql-stroke").length >= 2 &&
        (oldSvg.getAttribute("viewBox") === "0 0 18 18" ||
            oldSvg.getAttribute("viewbox") === "0 0 18 18");
    if (alreadyReplaced) return;

    const wrapper = document.createElement("div");
    wrapper.innerHTML = TABLE_BETTER_ICON;
    const newSvg = wrapper.firstElementChild;
    if (!newSvg) return;

    if (oldSvg) {
        oldSvg.replaceWith(newSvg);
    } else {
        button.insertBefore(newSvg, button.firstChild);
    }

    if (existingSelect && !button.contains(existingSelect)) {
        button.appendChild(existingSelect);
    }
};

const patchTableBetterCreateDefaults = () => {
    const globalRef = globalThis as typeof globalThis & Record<string, unknown>;
    if (globalRef[TABLE_BETTER_CREATE_DEFAULTS_PATCHED_KEY]) return;

    const formatNames = [
        "table-cell",
        "table-th",
        "table-temporary",
        "table-col",
        "table-list-container",
    ];

    formatNames.forEach((formatName) => {
        try {
            const blot = Quill.import(`formats/${formatName}`) as {
                create?: (value?: unknown) => HTMLElement;
                __tableCreateDefaultsPatched?: boolean;
            };
            if (!blot?.create || blot.__tableCreateDefaultsPatched) return;

            const create = blot.create.bind(blot);
            blot.create = (value?: unknown) => create(value ?? {});
            blot.__tableCreateDefaultsPatched = true;
        } catch {
            // Optional table formats may be unavailable until the plugin registers.
        }
    });

    globalRef[TABLE_BETTER_CREATE_DEFAULTS_PATCHED_KEY] = true;
};

const ensureTableBetterRegistered = () => {
    const globalRef = globalThis as typeof globalThis & Record<string, unknown>;
    if (!globalRef[TABLE_BETTER_REGISTERED_KEY]) {
        (QuillTableBetter as { register?: () => void }).register?.();
        (Quill as any).register(
            {
                "modules/table-better": QuillTableBetter,
            },
            true,
        );
        globalRef[TABLE_BETTER_REGISTERED_KEY] = true;
    }
    patchTableBetterCreateDefaults();
    // Always re-apply: plugin sets a filled icon at import time / HMR.
    applyTableBetterIcon();
};

const ensureSafeGetComputedStyle = () => {
    if (typeof window === "undefined") return;

    const globalRef = globalThis as typeof globalThis & Record<string, unknown>;
    if (globalRef[SAFE_GET_COMPUTED_STYLE_PATCHED_KEY]) return;

    const originalGetComputedStyle = window.getComputedStyle.bind(window);
    window.getComputedStyle = ((target: Element, pseudoElt?: string | null) => {
        const safeTarget =
            target instanceof Element ? target : (document.documentElement as Element);
        return originalGetComputedStyle(safeTarget, pseudoElt || undefined);
    }) as typeof window.getComputedStyle;

    globalRef[SAFE_GET_COMPUTED_STYLE_PATCHED_KEY] = true;
};

export interface WebpOptimizionType {
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
}

/** courseAbout: лише заголовки 2–4, жирний/курсив/підкреслення, маркований список; без зображень */
export type ReactQuillToolbarPreset = "default" | "courseAbout";

export interface ReactQuillToolbarLabels {
    pasteFromClipboard?: string;
    insertTemplateKey?: string;
}

const useReactQuillSimple = (
    webpOptimizion: WebpOptimizionType | null = null,
    uploadImageFunction?: QuillImageUploadFunction,
    uploadPastedImage?: QuillImageUploadFunction,
    toolbarPreset: ReactQuillToolbarPreset = "default",
    onSelectKey?: QuillSelectKeyHandler,
    toolbarLabels?: ReactQuillToolbarLabels,
) => {
    const webpQuality = webpOptimizion?.quality || WEBP_QUALITY_DEF;
    const maxW = webpOptimizion?.maxWidth || WEBP_MAXWIDTH_DEF;
    const maxH = webpOptimizion?.maxHeight || WEBP_MAXHEIGHT_DEF;
    ensureTableBetterRegistered();
    ensureSoftBreakRegistered();
    ensureSafeGetComputedStyle();

    const rqRef = useRef<ReactQuill | null>(null);
    const quillRef = useRef<Quill | null>(null);
    const uploadImageRef = useRef<QuillImageUploadFunction | undefined>(uploadImageFunction);
    const uploadPastedRef = useRef<QuillImageUploadFunction | undefined>(uploadPastedImage);
    uploadImageRef.current = uploadImageFunction;
    uploadPastedRef.current = uploadPastedImage;

    const getUploadImageFn = useCallback((): QuillImageUploadFunction | undefined => {
        const pasted = uploadPastedRef.current;
        const image = uploadImageRef.current;
        if (typeof pasted === "function") return pasted;
        if (typeof image === "function") return image;
        return undefined;
    }, []);

    const [editor, setEditor] = useState<Quill | null>(null);
    const [isBusy, setIsBusy] = useState(false);

    const syncEditorRef = useCallback((instance: ReactQuill | null) => {
        if (!instance) {
            quillRef.current = null;
            setEditor(null);
            return null;
        }

        try {
            const q = instance.getEditor() as unknown as Quill;
            quillRef.current = q;
            setEditor((prev) => (prev === q ? prev : q));
            return q;
        } catch {
            return null;
        }
    }, []);

    const getSafeEditor = useCallback(() => {
        if (quillRef.current) return quillRef.current;
        return syncEditorRef(rqRef.current);
    }, [syncEditorRef]);

    const attachRef = useCallback(
        (instance: ReactQuill | null) => {
            rqRef.current = instance;
            if (!instance) {
                syncEditorRef(null);
                return;
            }

            if (syncEditorRef(instance)) return;

            requestAnimationFrame(() => {
                if (rqRef.current === instance) syncEditorRef(instance);
            });
        },
        [syncEditorRef],
    );

    const imageHandler = useCallback(async function (this: { quill: Quill }) {
        const quill = this?.quill || getSafeEditor();
        if (!quill) return;

        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;

            let imageSrc = "";
            try {
                setIsBusy(true);
                setQuillBusy(quill, true);
                imageSrc = await resolveEditorImageSrc(file, {
                    webpQuality,
                    maxW,
                    maxH,
                    uploadImage: getUploadImageFn(),
                });
            } finally {
                setQuillBusy(quill, false);
                setIsBusy(false);
            }

            // With Cloudflare configured, never embed local data: URLs.
            if (!imageSrc) return;
            if (getUploadImageFn() && imageSrc.startsWith("data:")) return;
            const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
            quill.insertEmbed(range.index, "image", imageSrc, "user");
            quill.setSelection(range.index + 1, 0, "user");
        };
        input.click();
    }, [getSafeEditor, getUploadImageFn, maxH, maxW, webpQuality]);

    /**
     * Quill clipboard routes Ctrl+V / drop image files here (not through our paste listener).
     * Default handler = FileReader → data URL — that is the bug we replace.
     */
    const quillUploaderHandlerRef = useRef<
        (this: { quill: Quill }, range: { index: number; length: number }, files: File[]) => void | Promise<void>
    >(() => undefined);

    quillUploaderHandlerRef.current = async function (
        this: { quill: Quill },
        range: { index: number; length: number },
        files: File[],
    ) {
        const quill = this?.quill || getSafeEditor();
        if (!quill || !files?.length) return;

        // Snapshot selection before busy — quill.enable(false) drops "user" edits.
        const index = range?.index ?? quill.getSelection(true)?.index ?? quill.getLength();
        const deleteLen = range?.length || 0;
        const uploadFn = getUploadImageFn();
        const imageSrcs: string[] = [];

        setIsBusy(true);
        setQuillBusy(quill, true);
        try {
            for (const raw of files) {
                const file = normalizePastedImageFile(raw);
                const imageSrc = await resolveEditorImageSrc(file, {
                    webpQuality,
                    maxW,
                    maxH,
                    uploadImage: uploadFn,
                });
                if (!imageSrc) continue;
                if (uploadFn && imageSrc.startsWith("data:")) continue;
                imageSrcs.push(imageSrc);
            }
        } finally {
            setQuillBusy(quill, false);
            setIsBusy(false);
        }

        // Insert only after re-enable (same order as toolbar imageHandler).
        if (!imageSrcs.length) return;
        let cursor = index;
        if (deleteLen > 0) {
            quill.deleteText(cursor, deleteLen, "user");
        }
        for (const imageSrc of imageSrcs) {
            quill.insertEmbed(cursor, "image", imageSrc, "user");
            cursor += 1;
        }
        quill.setSelection(cursor, 0, "user");
    };

    const onSelectKeyRef = useRef(onSelectKey);
    onSelectKeyRef.current = onSelectKey;
    const enableInsertKey = typeof onSelectKey === "function";

    const pasteFromClipboardHandler = useCallback(async function (this: { quill: Quill }) {
        const quill = this?.quill || quillRef.current;
        if (!quill) return;

        const canReadClipboard =
            typeof navigator !== "undefined" &&
            navigator.clipboard &&
            typeof navigator.clipboard.readText === "function";
        if (!canReadClipboard) return;

        try {
            const text = await navigator.clipboard.readText();
            if (!text) return;

            const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
            quill.insertText(range.index, text, "user");
            quill.setSelection(range.index + text.length, 0, "user");
        } catch (error) {
            console.error("Clipboard text paste error:", error);
        }
    }, []);

    const insertTemplateKeyHandler = useCallback(function (this: { quill: Quill }) {
        const selectKey = onSelectKeyRef.current;
        if (typeof selectKey !== "function") return;

        const quill = this?.quill || quillRef.current;
        if (!quill) return;

        const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
        const savedRange = { index: range.index, length: range.length };

        selectKey((key: string) => {
            const text = String(key || "");
            if (!text) return;

            // Use the same Quill instance that opened the picker (ref may change after remount).
            quill.focus();
            if (savedRange.length > 0) {
                quill.deleteText(savedRange.index, savedRange.length, "user");
            }
            quill.insertText(savedRange.index, text, "user");
            quill.setSelection(savedRange.index + text.length, 0, "user");
        });
    }, []);

    const modules = useMemo(() => {
        const clipboardGroup = enableInsertKey
            ? ["insertTemplateKey", "pasteFromClipboard"]
            : ["pasteFromClipboard"];
        const clipboardHandlers = enableInsertKey
            ? {
                  insertTemplateKey: insertTemplateKeyHandler,
                  pasteFromClipboard: pasteFromClipboardHandler,
              }
            : {
                  pasteFromClipboard: pasteFromClipboardHandler,
              };

        const softBreakModules = {
            keyboard: {
                bindings: {
                    softBreak: softBreakKeyboardBinding,
                },
            },
            clipboard: {
                matchers: [["BR", softBreakClipboardMatcher]],
            },
        };

        const uploaderModule = {
            mimetypes: QUILL_UPLOADER_MIMETYPES,
            handler(
                this: { quill: Quill },
                range: { index: number; length: number },
                files: File[],
            ) {
                return quillUploaderHandlerRef.current.call(this, range, files);
            },
        };

        if (toolbarPreset === "courseAbout") {
            return {
                toolbar: {
                    container: [
                        [{ header: [2, 3, 4, false] }],
                        clipboardGroup,
                        ["bold", "italic", "underline"],
                        [{ list: "bullet" }],
                    ],
                    handlers: clipboardHandlers,
                },
                ...softBreakModules,
            };
        }
        return {
            toolbar: {
                container: [
                    [{ header: [2, 3, 4, 5, false] }],
                    clipboardGroup,
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["blockquote", "code-block"],
                    [{ color: [] }, { background: [] }],
                    [{ align: [] }],
                    ["link", "image", "table-better"],
                    ["clean"],
                ],
                handlers: {
                    ...clipboardHandlers,
                    image: imageHandler,
                },
            },
            // Ctrl+V / drop images go through Quill clipboard → uploader (not toolbar image handler).
            uploader: uploaderModule,
            table: false,
            "table-better": {
                language: "en_US",
                menus: ["column", "row", "merge", "table", "cell", "wrap", "copy", "delete"],
                toolbarTable: true,
                // Keep insert-key / paste enabled while editing a table cell.
                toolbarButtons: {
                    whiteList: [
                        ...TABLE_BETTER_TOOLBAR_WHITE_LIST,
                        ...TABLE_BETTER_TOOLBAR_CUSTOM_SINGLE,
                    ],
                    singleWhiteList: [
                        "link",
                        "image",
                        ...TABLE_BETTER_TOOLBAR_CUSTOM_SINGLE,
                    ],
                },
            },
            keyboard: {
                bindings: {
                    ...(QuillTableBetter as any).keyboardBindings,
                    softBreak: softBreakKeyboardBinding,
                },
            },
            clipboard: softBreakModules.clipboard,
        };
    }, [enableInsertKey, imageHandler, insertTemplateKeyHandler, pasteFromClipboardHandler, toolbarPreset]);

    useEffect(() => {
        if (!editor) return;

        const quill = editor;
        const root = editor.root;
        const handleClipboardImages = toolbarPreset !== "courseAbout";

        const onPaste = (e: ClipboardEvent) => {
            // Sync read — clipboardData is only reliable during the event.
            const captured = capturePasteData(e);

            // File image: stop Quill's default uploader (FileReader → data:) and use Cloudflare.
            if (handleClipboardImages && captured.imageFile) {
                stopPasteEvent(e);
                const range = quill.getSelection(true) || {
                    index: quill.getLength(),
                    length: 0,
                };
                void quillUploaderHandlerRef.current.call(
                    { quill },
                    range,
                    [captured.imageFile],
                );
                return;
            }

            const htmlImageOnly =
                handleClipboardImages && isImagePasteCapture(captured);
            const htmlPaste = Boolean(captured.html);

            if (!htmlImageOnly && !htmlPaste) return;

            stopPasteEvent(e);

            void (async () => {
                if (htmlImageOnly) setIsBusy(true);
                try {
                    await processCapturedPaste(captured, {
                        webpQuality,
                        maxW,
                        maxH,
                        quill,
                        uploadImage: getUploadImageFn(),
                        handleClipboardImages,
                    });
                } finally {
                    if (htmlImageOnly) setIsBusy(false);
                }
            })();
        };
        root.addEventListener("paste", onPaste as any, true);

        return () => {
            root.removeEventListener("paste", onPaste as any, true);
        };
    }, [editor, getUploadImageFn, maxH, maxW, toolbarPreset, webpQuality]);

    // Belt-and-suspenders: patch live Quill uploader (drop + any paste we don't capture).
    useEffect(() => {
        if (!editor || toolbarPreset === "courseAbout") return;

        const uploader = editor.getModule("uploader") as {
            options?: {
                mimetypes?: string[];
                handler?: (
                    this: { quill: Quill },
                    range: { index: number; length: number },
                    files: File[],
                ) => void;
            };
            upload?: (
                range: { index: number; length: number },
                files: FileList | File[],
            ) => void;
        } | null;
        if (!uploader?.options) return;

        uploader.options.mimetypes = QUILL_UPLOADER_MIMETYPES;
        uploader.options.handler = function (
            this: { quill: Quill },
            range: { index: number; length: number },
            files: File[],
        ) {
            return quillUploaderHandlerRef.current.call(
                { quill: this?.quill || editor },
                range,
                files,
            );
        };
        uploader.upload = function (
            range: { index: number; length: number },
            files: FileList | File[],
        ) {
            const uploads = Array.from(files || []).filter((file) => {
                if (!file) return false;
                const type = String(file.type || "").toLowerCase();
                if (!type) return true;
                return type.startsWith("image/") && type !== "image/tiff";
            });
            if (!uploads.length) return;
            uploader.options?.handler?.call({ quill: editor }, range, uploads);
        };
    }, [editor, toolbarPreset]);

    useEffect(() => {
        if (!editor || toolbarPreset === "courseAbout") return;
        applyTableBetterIcon();
        replaceTableBetterToolbarIcon(editor);
    }, [editor, toolbarPreset]);

    useEffect(() => {
        if (!editor || toolbarPreset === "courseAbout") return;

        const tableModule = editor.getModule("table-better") as Record<string, unknown> | null;
        if (!tableModule) return;

        const patchedFlag = tableModule[TABLE_BETTER_DELETE_GUARD_PATCHED_KEY];
        if (patchedFlag) return;

        const wrapWithNullGuard = (methodName: string) => {
            const method = tableModule[methodName];
            if (typeof method !== "function") return;

            tableModule[methodName] = function patchedTableMethod(this: unknown, ...args: unknown[]) {
                try {
                    return (method as Function).apply(this, args);
                } catch (error) {
                    const message = (error as Error)?.message || "";
                    const isKnownNullChildrenError = message.includes("Cannot read properties of null") && message.includes("children");
                    if (isKnownNullChildrenError) {
                        // quill-table-better bug while clearing column width in properties dialog.
                        return undefined;
                    }
                    throw error;
                }
            };
        };

        wrapWithNullGuard("handleDeleteKeyup");
        wrapWithNullGuard("removeSelectedTdContent");
        wrapWithNullGuard("removeSelectedTdsContent");
        wrapWithNullGuard("handleKeyup");
        tableModule[TABLE_BETTER_DELETE_GUARD_PATCHED_KEY] = true;
    }, [editor, toolbarPreset]);

    useEffect(() => {
        if (editor || !rqRef.current) return;

        let rafId = 0;
        let attempts = 0;
        const maxAttempts = 120;

        const trySync = () => {
            attempts += 1;
            if (syncEditorRef(rqRef.current)) return;
            if (attempts >= maxAttempts) return;
            rafId = requestAnimationFrame(trySync);
        };

        rafId = requestAnimationFrame(trySync);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [editor, syncEditorRef]);

    useEffect(() => {
        if (!editor) return;
        if (typeof window === "undefined") return;

        const ua = window.navigator.userAgent;
        const isSafari =
            /Safari/i.test(ua) &&
            !/Chrome|CriOS|Chromium|Android|FxiOS|EdgiOS/i.test(ua);
        if (!isSafari) return;

        const quill = editor;
        const root = quill.root;

        const ensureCaretVisible = () => {
            requestAnimationFrame(() => {
                if (!quill.hasFocus()) return;
                if (quill.getLength() > 1) return;

                const prevTransform = root.style.webkitTransform;
                root.style.webkitTransform = "translateZ(0)";
                void root.offsetHeight;
                root.style.webkitTransform = prevTransform;

                const selection = quill.getSelection();
                if (!selection || selection.index !== 0 || selection.length !== 0) {
                    quill.setSelection(0, 0, "silent");
                }
            });
        };

        root.addEventListener("focus", ensureCaretVisible, true);
        root.addEventListener("click", ensureCaretVisible, true);

        return () => {
            root.removeEventListener("focus", ensureCaretVisible, true);
            root.removeEventListener("click", ensureCaretVisible, true);
        };
    }, [editor]);

    useEffect(() => {
        if (!editor) return;
        const toolbar = editor.getModule("toolbar") as { container?: HTMLElement } | null;
        const container = toolbar?.container;
        if (!container) return;

        const pasteBtn = container.querySelector("button.ql-pasteFromClipboard") as HTMLButtonElement | null;
        if (pasteBtn) {
            const title = toolbarLabels?.pasteFromClipboard || "Paste";
            pasteBtn.title = title;
            pasteBtn.setAttribute("aria-label", title);
        }

        const keyBtn = container.querySelector("button.ql-insertTemplateKey") as HTMLButtonElement | null;
        if (keyBtn) {
            const title = toolbarLabels?.insertTemplateKey || "Insert key";
            keyBtn.title = title;
            keyBtn.setAttribute("aria-label", title);
        }
    }, [editor, enableInsertKey, modules, toolbarLabels?.pasteFromClipboard, toolbarLabels?.insertTemplateKey]);

    return {
        attachRef,
        modules,
        isBusy,
        editor,
    };
};

export default useReactQuillSimple;